/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { DELAYED_TOAST_CONSTANTS } from "constants/delayedToast";
import { MXN_ACTION_TYPE, RECORD_PERCIEVED_TIME_EVENTS } from "constants/menu";
import { Dispatch } from "react";
import Blaze from "@swiggy-private/blaze.js";
import type { IQrCodeAction, IReducerResponse } from "@swiggy-private/portal-dineout-context";
import type { NextRouter } from "next/router";
import { getBridgeSdk } from "./bridgeSdk";
import { isApp } from "./browser";
import { dispatchFilterAction } from "./clickHandlerHelpers";
import { filterCardsByAppliedFilters } from "./filterHelpers";
import { getToastBottomHeightMenuPage, getToastStyle, getToastWidth } from "./helper";
import {
    filterByCategoryId,
    filterByParentType,
    filterDietOptions,
    generateMenuCategoryWidgetData,
    getFilteredCardsByVeg,
    isCollectionEmpty,
} from "./menu";
import { DocumentWithViewTransition } from "../types";

interface IOnClickHandler {
    state: IReducerResponse;
    onAddButtonClick: (newItem: any, isCustomisable?: boolean, isCrossSellItem?: boolean) => void;
    toggleTheme?: VoidFunction;
    extraParams: Record<string, any>;
    dispatch: Dispatch<IQrCodeAction>;
    context?: "menu" | "collections"; // New optional parameter for context
    collectionId?: string; // Required when context is 'collections'
    router?: NextRouter | null;
    onShowMultiVariantPrompt?: VoidFunction;
    onBillPayment?: () => void | Promise<boolean>; // Callback for bill payment action
}

export const onClickHandler = ({
    state,
    onAddButtonClick,
    toggleTheme,
    extraParams,
    dispatch,
    context = "menu", // Default to 'menu' for backward compatibility
    collectionId,
    router,
    onShowMultiVariantPrompt,
    onBillPayment,
}: IOnClickHandler) => {
    // Get the appropriate data source based on context
    const isCollectionsContext = context === "collections";

    const currentData = isCollectionsContext ? state.collections[collectionId as string] : state.menu;
    const { initialData } = currentData;

    const {
        action,
        name = "",
        categoryId,
        preference,
        type,
        newItem,
        cta,
        quickAction = {},
        applyFilters,
        cartItemsCount,
        isDecrement,
        isCustomisable,
        itemInfo,
    } = extraParams || {};
    const itemWithName = newItem ? { ...newItem, name: newItem.name || itemInfo?.name || "" } : newItem;
    switch (action || type) {
        case MXN_ACTION_TYPE.HALF_CARD_CONTENT_APPLY_FILTERS:
        case MXN_ACTION_TYPE.PILL_SELECTED_FILTERS_CLICK:
        case MXN_ACTION_TYPE.PILL_UNSELECTED_FILTERS_CLICK:
        case MXN_ACTION_TYPE.CLEAR_FILTERS: {
            Blaze.recordStart(RECORD_PERCIEVED_TIME_EVENTS.MENU_APPLY_FILTERS);
            const { filteredData, appliedFilters } = applyFilters?.(extraParams);

            dispatchFilterAction({
                dispatch,
                actionType: "PREFERENCE",
                payload: {
                    filteredData,
                    preference: "DEFAULT",
                    emptyItemCard: isCollectionEmpty(filteredData),
                    appliedFilters,
                },
                isCollectionsContext,
            });

            Blaze.recordEnd(RECORD_PERCIEVED_TIME_EVENTS.MENU_APPLY_FILTERS, { action: action || type });
            return;
        }
        case MXN_ACTION_TYPE.PILLS:
            // Follow a series of filtering steps and update accordingly
            // 1. Filter cards by parentType
            // 2. Then filter categories by parentType
            // 3. Filter for veg
            // 4. Then update MenuCategory Widget
            // 5. Then filter cards by categoryId
            const filteredCardsByParentType = filterByParentType(initialData, name);
            const preferenceSpecificCategories = getFilteredCardsByVeg(
                filteredCardsByParentType,
                currentData.preference,
            );
            const appliedFilteredData = filterCardsByAppliedFilters(
                preferenceSpecificCategories,
                currentData.appliedFilters,
            );
            const menuCategoryWidgetData2 = generateMenuCategoryWidgetData(appliedFilteredData, "");
            const menuDataAfterDietFilters = filterDietOptions(menuCategoryWidgetData2, name);

            dispatchFilterAction({
                dispatch,
                isCollectionsContext,
                collectionId,
                actionType: "PARENT_TYPE",
                payload: {
                    filteredData: menuDataAfterDietFilters,
                    parentType: name,
                    emptyItemCard: isCollectionEmpty(menuDataAfterDietFilters),
                },
            });
            break;

        case MXN_ACTION_TYPE.CATEGORY: {
            if (categoryId === currentData.categoryId) {
                // 1. Filter cards by parentType
                // 2. Then generateMenuCategoryWidgetData
                // 3. Then filter cards by categoryId
                const cardsFilteredByParent = filterByParentType(initialData, currentData.parentType);
                const preferenceSpecificCategories2 = getFilteredCardsByVeg(
                    cardsFilteredByParent,
                    currentData.preference,
                );
                const filteredData = filterCardsByAppliedFilters(
                    preferenceSpecificCategories2,
                    currentData.appliedFilters,
                );
                const menuCategoryWidgetData3 = generateMenuCategoryWidgetData(filteredData, "");
                const cardsFilteredByCategoryId = filterByCategoryId(menuCategoryWidgetData3, "");

                dispatchFilterAction({
                    dispatch,
                    isCollectionsContext,
                    collectionId,
                    actionType: "CATEGORY",
                    payload: {
                        filteredData: cardsFilteredByCategoryId,
                        categoryId: "",
                        emptyItemCard: isCollectionEmpty(cardsFilteredByCategoryId),
                    },
                });
                break;
            }

            // 1. Filter cards by parentType
            // 2. Then generateMenuCategoryWidgetData
            // 3. Then filter cards by categoryId
            const cardsFilteredByParent = filterByParentType(initialData, currentData.parentType);
            const preferenceSpecificCategories2 = getFilteredCardsByVeg(cardsFilteredByParent, currentData.preference);
            const filteredData = filterCardsByAppliedFilters(preferenceSpecificCategories2, currentData.appliedFilters);
            const menuCategoryWidgetData3 = generateMenuCategoryWidgetData(filteredData, categoryId);
            const cardsFilteredByCategoryId = filterByCategoryId(menuCategoryWidgetData3, categoryId);

            dispatchFilterAction({
                dispatch,
                isCollectionsContext,
                collectionId,
                actionType: "CATEGORY",
                payload: {
                    filteredData: cardsFilteredByCategoryId,
                    categoryId,
                    emptyItemCard: isCollectionEmpty(cardsFilteredByCategoryId),
                },
            });
            break;
        }

        case MXN_ACTION_TYPE.UPDATE_CUTOMIZE_ITEM_HANDLER:
            onAddButtonClick(itemWithName, true);
            break;
        case MXN_ACTION_TYPE.UPDATE_CROSS_SELL_CUTOMIZE_ITEM_HANDLER:
            onAddButtonClick(itemWithName, true, true);
            break;
        case MXN_ACTION_TYPE.UPDATE_ITEM_HANDLER:
        case MXN_ACTION_TYPE.ADD_CUTOMIZE_ITEM_HANDLER: {
            // Validate cartItemsCount is a number
            const validCartItemsCount = typeof cartItemsCount === "number" ? cartItemsCount : 0;
            //Is user clicking "−" && Does this item have MORE than 1 customization in cart? && Is the callback function provided?
            if (isDecrement && validCartItemsCount > 1 && onShowMultiVariantPrompt) {
                onShowMultiVariantPrompt();
                return;
            }

            onAddButtonClick(itemWithName, isCustomisable);
            break;
        }
        case MXN_ACTION_TYPE.UPDATE_CROSS_SELL_ITEM_HANDLER: {
            const validCartItemsCount = typeof cartItemsCount === "number" ? cartItemsCount : 0;
            if (isDecrement && validCartItemsCount > 1 && onShowMultiVariantPrompt) {
                onShowMultiVariantPrompt();
                return;
            }

            onAddButtonClick(itemWithName, isCustomisable, true);
            break;
        }

        case MXN_ACTION_TYPE.VEG_NONVEG:
            if (preference === "DEFAULT") {
                // 1. Filter cards by parentType
                // 2. Then generateMenuCategoryWidgetData
                // 3. Then filter cards by categoryId
                const cardsFilteredForVeg = filterByParentType(initialData, currentData.parentType);
                const menuCategoryWidgetData4 = generateMenuCategoryWidgetData(
                    cardsFilteredForVeg,
                    currentData.categoryId,
                );
                const finalFilteredData = filterByCategoryId(menuCategoryWidgetData4, currentData.categoryId);

                dispatchFilterAction({
                    dispatch,
                    isCollectionsContext,
                    collectionId,
                    actionType: "PREFERENCE",
                    payload: {
                        filteredData: finalFilteredData,
                        preference: "DEFAULT",
                        emptyItemCard: isCollectionEmpty(finalFilteredData),
                    },
                });
                break;
            }

            // 1. Filter cards by parentType
            // 2. Then filter categories by parentType
            // 3. Filter for veg
            // 4. Then update MenuCategory Widget
            // 5. Then filter cards by categoryId
            const cardsFilteredForVeg = filterByParentType(initialData, currentData.parentType);
            const vegSpecificCategories = getFilteredCardsByVeg(cardsFilteredForVeg, preference);
            const menuCategoryWidgetData = generateMenuCategoryWidgetData(vegSpecificCategories);
            const finalFilteredData = filterByCategoryId(menuCategoryWidgetData, currentData.categoryId);

            dispatchFilterAction({
                dispatch,
                isCollectionsContext,
                collectionId,
                actionType: "PREFERENCE",
                payload: {
                    filteredData: finalFilteredData,
                    preference,
                    emptyItemCard: isCollectionEmpty(finalFilteredData),
                },
            });
            break;

        case MXN_ACTION_TYPE.MENU_BACK:
            if (isApp()) {
                getBridgeSdk().close();
            } else {
                window.location.href = "https://www.swiggy.com/dineout";
            }
            break;

        case MXN_ACTION_TYPE.FAB_WIDGET:
            const isCartItemAvailable = state.cart.localCart.cartItems.length > 0;
            const bottomHeight = getToastBottomHeightMenuPage(isCartItemAvailable);
            const toastWidth = getToastWidth(
                3 * DELAYED_TOAST_CONSTANTS.DEFAULT_HORIZONTAL_MARGIN + DELAYED_TOAST_CONSTANTS.TOAST_DEFAULT_SIZE,
            );
            let sab = "0px";
            try {
                const rootStyle = getComputedStyle(document.documentElement);
                sab = rootStyle.getPropertyValue("--sab")?.trim() || "0px";
            } catch {
                // Silently fail if not in browser environment
            }
            quickAction.style = getToastStyle(bottomHeight, toastWidth, sab);
            quickAction.successIcon = "portal/static-assets/images/digital-menu/all-theme-assets/tick.png";
            extraParams?.onQuickActionButtonClick?.(quickAction);
            break;

        case MXN_ACTION_TYPE.TOGGLE_THEME: {
            // Use View Transitions API for circular reveal animation
            const doc = document as DocumentWithViewTransition;
            if (doc.startViewTransition) {
                doc.startViewTransition(() => {
                    toggleTheme?.();
                });
            } else {
                // Instant theme change for browsers without View Transitions API
                toggleTheme?.();
            }
            break;
        }

        case MXN_ACTION_TYPE.BILL_PAYMENT: {
            onBillPayment?.();
            break;
        }

        default: {
            if (cta?.link) {
                router?.push(cta?.link);
            }
            return "";
        }
    }
};
